import { GlobalPeriodSelect } from '@components'

import { Button, ButtonDropdown, notify, notifyProgress, Text } from '@billy/design-system'
import isNumber from 'lodash/isNumber'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { PeriodMode } from '../../../../enums/periodMode'
import { useOrganizationSource, useSegment } from '../../../../hooks'
import { PeriodValue } from '../../../../types/periodValue'
import { BackupTransactionType } from '../../enums/backupTransationType'
import { useExportBackup } from '../../hooks/useExportBackup'
import { useExportsPeriodValue } from '../../hooks/useExportsPeriodValue'
import { getBackupItems } from '../../utils/getBackupItems'
import { ExportsCard } from '../ExportsCard'

export const Backup = () => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { encodedPeriodValue, period, setPeriod } = useExportsPeriodValue()
  const { isSourceZervant } = useOrganizationSource()
  const { track } = useSegment()

  const [transactionType, setTransactionType] = useState(BackupTransactionType.All)

  const organizationId = organization?.id

  const { items } = getBackupItems({ t, isSourceZervant })

  const { mutate } = useExportBackup({
    onSuccess: (response) => {
      if (response instanceof Response) {
        const progressEventsUrl = response.headers.get('Location')

        if (!progressEventsUrl) {
          return
        }

        const eventSource = new EventSource(progressEventsUrl)

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)

            const progressPercent = data?.progress && isNumber(data?.progress) ? Math.round(data.progress * 100) : 0

            if (!data.isCompleted) {
              notifyProgress.update({
                id: NotificationKeys.ExportsBackupSend,
                title: t('exports.card.backup.sent.notification.title'),
                description: t('exports.card.backup.sent.notification.description'),
                progress: progressPercent,
                closable: true,
                variant: 'success',
                onClick: () => {
                  eventSource.close()
                },
              })
            } else {
              notifyProgress.update({
                id: NotificationKeys.ExportsBackupSend,
                title: t('exports.card.backup.sent.notification.title'),
                description: t('exports.card.backup.sent.notification.success.description'),
                progress: progressPercent,
                closable: true,
                variant: 'success',
                onClick: () => {
                  window.open(data.downloadUrl, '_blank')
                },
              })
            }

            if (data.isCompleted) {
              eventSource.close()
            }
          } catch {
            eventSource.close()

            notify({
              id: NotificationKeys.ExportsBackupSend,
              message: t('exports.card.backup.sent.notification.error'),
              variant: 'error',
            })
          }
        }
      }
    },
    onError: () => {
      notify({
        id: NotificationKeys.ExportsBackupSend,
        message: t('exports.card.backup.sent.notification.error'),
        variant: 'error',
      })
    },
  })

  const handleChangePeriod = useCallback(
    (newPeriod: PeriodValue) => {
      setPeriod(newPeriod)
    },
    [setPeriod],
  )

  const handleSelectTransactionType = useCallback(
    (_id: string, value: BackupTransactionType) => {
      setTransactionType(value)
    },
    [setTransactionType],
  )

  const getPeriod = useCallback(() => {
    const { customDate, mode, value } = period

    if (mode === PeriodMode.FiscalYear && isNumber(value)) {
      return { period: `year:${value}` }
    }

    if (mode !== PeriodMode.All || customDate) {
      return { period: encodedPeriodValue }
    }

    return {}
  }, [encodedPeriodValue, period])

  const handleClickDownload = useCallback(() => {
    if (!organizationId) {
      return
    }

    notifyProgress({
      id: NotificationKeys.ExportsBackupSend,
      description: t('exports.card.backup.sent.notification.description'),
      progress: 0,
      closable: true,
      variant: 'success',
    })

    mutate({
      organizationId,
      types: transactionType,
      ...(encodedPeriodValue && getPeriod()),
    })

    track('XXX - Mikkel - Exports', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      export_type: 'Backup',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      file_format: 'zip',
      period: period.value,
    })
  }, [encodedPeriodValue, getPeriod, mutate, organizationId, period, t, track, transactionType])

  return (
    <ExportsCard
      description={t('exports.card.backup.description')}
      title={t('exports.card.backup.title')}
      actionButton={
        <Button onClick={handleClickDownload} variant="outlined">
          {t('download')}
        </Button>
      }
    >
      <div>
        <Text colorVariant="label" weight="medium">
          {t('exports.period')}
        </Text>
        <GlobalPeriodSelect encodedPeriod={encodedPeriodValue} onChange={handleChangePeriod} fullWidth />
        <Text colorVariant="label" weight="medium">
          {t('exports.card.backup.transaction_type.dropdown.label')}
        </Text>
        <ButtonDropdown
          variant="outlined"
          fullWidth
          items={items}
          onSelect={handleSelectTransactionType}
          selectedId={transactionType}
        />
      </div>
    </ExportsCard>
  )
}
